import React, {ReactElement, useState} from 'react';
import {Box, Divider, Stack, Typography} from '@mui/material';
import moment from 'moment';
import {NewOccurrenceReportEditorProps} from '../../../../@types/editor-props';
import NewOccurrenceReportLinkedDescriptorsEditor from './NewOccurrenceReportLinkedDescriptorsEditor';
import HighlightedNarrative from '../../../common/HighlightedNarrative.component';

const EXCLUDED_FIELDS = new Set(['narrative', 'event_sequences']);

const isScalar = (value: unknown): boolean =>
    value !== null && value !== undefined && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean');

const formatFieldName = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function NewOccurrenceReportCombinedEditor(props: NewOccurrenceReportEditorProps): ReactElement {
    const {occurrence} = props;
    const [hoveredPhrases, setHoveredPhrases] = useState<string[]>([]);

    const content = occurrence.content && typeof occurrence.content === 'object'
        ? occurrence.content as Record<string, unknown>
        : null;

    const narrative = content?.narrative as string | undefined;

    const kvPairs: Array<[string, string]> = content
        ? Object.entries(content)
            .filter(([key, value]) => !EXCLUDED_FIELDS.has(key) && isScalar(value) && value !== '')
            .map(([key, value]) => [key, String(value)])
        : [];

    return (
        <Box sx={{height: '100%', overflowY: 'auto'}}>
            {/* Report identity header */}
            <Box sx={{px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-start">
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                            Source
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>{occurrence.sourceName}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                            ID
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{fontFamily: 'monospace'}}>{occurrence.uniqueId}</Typography>
                    </Box>
                    {occurrence.dateTime && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                                Date / Time
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {moment(occurrence.dateTime).format('MMM D, YYYY HH:mm')}
                            </Typography>
                        </Box>
                    )}
                    {occurrence.facilityName && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                                Facility
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>{occurrence.facilityName}</Typography>
                        </Box>
                    )}
                    {(occurrence.latitude != null || occurrence.longitude != null) && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                                Location
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{fontFamily: 'monospace'}}>
                                {occurrence.latitude?.toFixed(4)}, {occurrence.longitude?.toFixed(4)}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Box>

            {/* Content KV grid */}
            {kvPairs.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {kvPairs.map(([key, value]) => (
                        <Box key={key} sx={{py: 0.75, pr: 2}}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem'}}
                            >
                                {formatFieldName(key)}
                            </Typography>
                            <Typography variant="body2">{value}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Narrative */}
            {narrative && (
                <Box sx={{px: 2, pt: 2, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1}}
                    >
                        Narrative
                    </Typography>
                    <HighlightedNarrative narrative={narrative} highlightPhrases={hoveredPhrases} />
                </Box>
            )}

            {/* Descriptors section */}
            <Box>
                <Stack direction="row" alignItems="center" gap={2} px={2} py={1.5} sx={{borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Typography variant="overline" fontWeight={600} color="text.secondary" sx={{letterSpacing: '0.12em', lineHeight: 1}}>
                        Descriptors
                    </Typography>
                    <Divider sx={{flex: 1}} />
                </Stack>
                <NewOccurrenceReportLinkedDescriptorsEditor
                    occurrence={occurrence}
                    onPhrasesHover={setHoveredPhrases}
                />
            </Box>
        </Box>
    );
}
